import { type LocalDate } from "@despejo/domain";
import { type EngineParameter, type UsedParameter, decimalField, pickParameter, usedParameter } from "./params.js";
import type { RtsActivity, RtsFactor, RtsFactorTable, RtsLimits } from "./rts.js";

/**
 * RTS parameters (plan §16, docs/tax/RTS.md §3-§4) resolved from
 * parameter_versions rows:
 *
 * - RTS_FACTORS  value { factors: { <activity>: { renta, iva?, ivaByRate?, rentaPerKm? } } }
 *   (Decreto 43881-H art. 5; DGT may vary them, art. 12).
 * - RTS_LIMITS   value { purchases_salarios_base, fixed_assets_salarios_base,
 *   fishing_fuel_salarios_base, max_persons, max_persons_microenterprise,
 *   max_establishments } (Decreto 43881-H art. 3 as amended by 45209-H).
 * - SALARIO_BASE value { amount }.
 *
 * When the catalog has no row for RTS_FACTORS or RTS_LIMITS, the figures of
 * docs/tax/RTS.md are used and the result is provisional: those tables are
 * still [verify] (factor layout from an OCR'd consolidated text), so they can
 * never make a result final.
 */

/** docs/tax/RTS.md §4 (Decreto 43881-H art. 5, consolidated text; retail layout [verify]). */
export const RTS_FACTORS_DOCUMENTED: RtsFactorTable = {
  version: "docs/tax/RTS.md §4 (sin versión en el catálogo)",
  approved: false,
  factors: {
    a: { renta: "0.01", ivaByRate: { "0.13": "0.02", "0.02": "0.0033", "0.01": "0.00125" } },
    b: { renta: "0.02", iva: "0.04" },
    c: { renta: "0.02", iva: "0.04" },
    d: { renta: "0.01", iva: "0.025" },
    e: { renta: "0.01", iva: "0.02" },
    f: { renta: "0.01", ivaByRate: { "0.13": "0.02", "0.01": "0.00125" } },
    g: { renta: "0.02", iva: "0.025" },
    h: { renta: "0.01", iva: "0.026" },
    i: { renta: "0.01", iva: "0.065" },
    j: { renta: "0.01", iva: "0.052" },
    k: { renta: "0.02", iva: "0.03" },
    l: { renta: "0.02", iva: "0.03" },
    m: { renta: "0.01", iva: "0.02" },
    n: { renta: "0.02", iva: "0.03" },
    o: { renta: "0.02", iva: "0.03" },
    p: { renta: "0.01", iva: "0.058" },
    q: { renta: "0.01", iva: "0.02" },
    r: { renta: "0.02", iva: "0.03" },
    s: { renta: "0.02", iva: "0.03" },
    t: { renta: "0.025", iva: "0.02" },
    u: { renta: "0.033", iva: "0.02" },
    v: { renta: "0", iva: "0", rentaPerKm: "0.50" },
  },
};

export interface RtsLimitSet extends RtsLimits {
  fishingFuelSalariosBase: string;
  maxPersonsMicroenterprise: number;
}

/** docs/tax/RTS.md §3 (Decreto 43881-H art. 3 as amended by 45209-H; Ley 10512 for SIEC/Pympa). */
export const RTS_LIMITS_DOCUMENTED: RtsLimitSet = {
  purchasesSalariosBase: "186",
  fixedAssetsSalariosBase: "350",
  fishingFuelSalariosBase: "8",
  maxPersons: 6,
  maxPersonsMicroenterprise: 5,
  maxEstablishments: 1,
};

/** Activity names (Decreto 43881-H art. 1, docs/tax/RTS.md §2). */
export const RTS_ACTIVITIES: { code: RtsActivity; name: string }[] = [
  { code: "a", name: "Comercio minorista" },
  { code: "b", name: "Bares" },
  { code: "c", name: "Preparación y venta de alimentos y bebidas" },
  { code: "d", name: "Repostería" },
  { code: "e", name: "Panaderías" },
  { code: "f", name: "Pequeños agricultores de ferias del agricultor" },
  { code: "g", name: "Viveros" },
  { code: "h", name: "Calzado y bolsos artesanales" },
  { code: "i", name: "Muebles artesanales" },
  { code: "j", name: "Estructuras metálicas" },
  { code: "k", name: "Bisutería" },
  { code: "l", name: "Artesanías y obras de arte" },
  { code: "m", name: "Alfarería y cerámica" },
  { code: "n", name: "Textiles para personas" },
  { code: "o", name: "Textiles y accesorios para mascotas" },
  { code: "p", name: "Floristerías" },
  { code: "q", name: "Estudios fotográficos" },
  { code: "r", name: "Serigrafía" },
  { code: "s", name: "Sublimación" },
  { code: "t", name: "Pesca artesanal en pequeña escala" },
  { code: "u", name: "Pesca artesanal en mediana escala" },
  { code: "v", name: "Taxi" },
];

export interface RtsParameters {
  table: RtsFactorTable;
  limits: RtsLimitSet;
  /** Salario base in force on the date asked for; null when the catalog has none (eligibility cannot be measured). */
  salarioBase: string | null;
  used: UsedParameter[];
  provisional: boolean;
  provisionalReasons: string[];
}

const DECIMAL = /^-?\d+(\.\d+)?$/;

function factorOf(raw: unknown, activity: string): RtsFactor {
  if (!raw || typeof raw !== "object") throw new TypeError(`RTS_FACTORS.${activity} must be an object`);
  const o = raw as Record<string, unknown>;
  const f: RtsFactor = { renta: decimalField(o, "renta") };
  if (o.iva !== undefined) f.iva = decimalField(o, "iva");
  if (o.rentaPerKm !== undefined) f.rentaPerKm = decimalField(o, "rentaPerKm");
  if (o.ivaByRate !== undefined) {
    const by = o.ivaByRate as Record<string, unknown>;
    f.ivaByRate = {};
    for (const [rate, v] of Object.entries(by)) {
      if (typeof v !== "string" || !DECIMAL.test(v)) throw new TypeError(`RTS_FACTORS.${activity}.ivaByRate.${rate} must be a decimal string`);
      f.ivaByRate[rate] = v;
    }
  }
  return f;
}

function intField(v: Record<string, unknown>, field: string, fallback: number): number {
  const x = v[field];
  if (x === undefined) return fallback;
  if (typeof x !== "number" || !Number.isInteger(x) || x < 0) throw new TypeError(`parameter field ${field} must be a non-negative integer`);
  return x;
}

/** Resolve the RTS factor table, limits and salario base in force on `on` (usually the quarter's last day). */
export function rtsParameters(rows: readonly EngineParameter[], on: LocalDate): RtsParameters {
  const used: UsedParameter[] = [];
  const reasons: string[] = [];

  let table = RTS_FACTORS_DOCUMENTED;
  const fRow = pickParameter(rows, "RTS_FACTORS", on);
  if (fRow) {
    used.push(usedParameter(fRow));
    const factors: RtsFactorTable["factors"] = {};
    for (const [k, v] of Object.entries((fRow.value.factors ?? {}) as Record<string, unknown>)) factors[k as RtsActivity] = factorOf(v, k);
    table = { version: `RTS_FACTORS v${fRow.version ?? "?"}`, approved: fRow.review_status === "APPROVED", factors };
    if (!table.approved) reasons.push(`Factores RTS en borrador (RTS_FACTORS v${fRow.version ?? "?"})`);
  } else {
    reasons.push("No hay factores RTS en el catálogo: se usan los de docs/tax/RTS.md, pendientes de verificar contra La Gaceta");
  }

  let limits = RTS_LIMITS_DOCUMENTED;
  const lRow = pickParameter(rows, "RTS_LIMITS", on);
  if (lRow) {
    used.push(usedParameter(lRow));
    const v = lRow.value;
    limits = {
      purchasesSalariosBase: decimalField(v, "purchases_salarios_base"),
      fixedAssetsSalariosBase: decimalField(v, "fixed_assets_salarios_base"),
      fishingFuelSalariosBase: v.fishing_fuel_salarios_base === undefined ? RTS_LIMITS_DOCUMENTED.fishingFuelSalariosBase : decimalField(v, "fishing_fuel_salarios_base"),
      maxPersons: intField(v, "max_persons", RTS_LIMITS_DOCUMENTED.maxPersons),
      maxPersonsMicroenterprise: intField(v, "max_persons_microenterprise", RTS_LIMITS_DOCUMENTED.maxPersonsMicroenterprise),
      maxEstablishments: intField(v, "max_establishments", RTS_LIMITS_DOCUMENTED.maxEstablishments),
    };
    if (lRow.review_status !== "APPROVED") reasons.push(`Límites RTS en borrador (RTS_LIMITS v${lRow.version ?? "?"})`);
  } else {
    reasons.push("No hay límites RTS en el catálogo: se usan los de docs/tax/RTS.md §3");
  }

  let salarioBase: string | null = null;
  const sRow = pickParameter(rows, "SALARIO_BASE", on);
  if (sRow) {
    used.push(usedParameter(sRow));
    salarioBase = decimalField(sRow.value, "amount");
    if (sRow.review_status !== "APPROVED") reasons.push(`Salario base en borrador (SALARIO_BASE v${sRow.version ?? "?"})`);
  } else {
    reasons.push(`No hay salario base vigente al ${on}`);
  }

  return { table, limits, salarioBase, used, provisional: reasons.length > 0, provisionalReasons: reasons };
}
